"use client";

import React from "react";
import { Box, Container, Typography, Button, Grid, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useColorScheme } from "@mui/material/styles";
import Link from "next/link";

const AboutSection = styled(Box)<{ isDarkMode: boolean }>(
  ({ theme, isDarkMode }) => ({
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(10),
    backgroundColor: isDarkMode ? "#161b22" : theme.palette.background.paper,
  })
);

const About: React.FC = () => {
  const { mode, systemMode } = useColorScheme();
  const isDarkMode =
    mode === "dark" || (mode === "system" && systemMode === "dark");

  return (
    <AboutSection isDarkMode={isDarkMode}>
      <Container maxWidth="lg">
        <Grid container spacing={8} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={4} alignItems="center" textAlign="center">
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  fontWeight: 600,
                  mb: 2,
                  color: isDarkMode ? "#ffffff" : "text.primary",
                }}
              >
                Why choose Bouncepoint?
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.125rem",
                  lineHeight: 1.6,
                  color: isDarkMode ? "#b8b8b8" : "text.secondary",
                  mb: 3,
                  maxWidth: "800px",
                }}
              >
                Bouncepoint was founded by an insolvency professional who knew
                the work could be faster, simpler, and more scalable — with the
                right tools.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.125rem",
                  lineHeight: 1.6,
                  color: isDarkMode ? "#b8b8b8" : "text.secondary",
                  mb: 4,
                  maxWidth: "800px",
                }}
              >
                Receivers already know how to get the job done. Bouncepoint just
                gives them the modern infrastructure to do it better.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.125rem",
                  lineHeight: 1.6,
                  color: isDarkMode ? "#b8b8b8" : "text.secondary",
                  mb: 4,
                  maxWidth: "800px",
                }}
              >
                We centralize fragmented workflows, eliminate redundancy, and
                automate critical steps — including stakeholder updates and
                compliance tracking — so you never miss a detail and can
                confidently grow your caseload.
              </Typography>

              <Link
                href="/contactUs"
                passHref
                style={{ textDecoration: "none" }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    mt: 2,
                    py: 1.5,
                    px: 3,
                    borderColor: "#42A6F6",
                    color: "#42A6F6",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#42A6F6",
                      color: "white",
                      borderColor: "#42A6F6",
                    },
                    ...(isDarkMode && {
                      borderColor: "#42A6F6",
                      color: "#42A6F6",
                      backgroundColor: "rgba(66, 166, 246, 0.1)",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "#42A6F6",
                        color: "#1a1a1a",
                        borderColor: "#42A6F6",
                      },
                    }),
                  }}
                >
                  Learn more
                </Button>
              </Link>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </AboutSection>
  );
};

export default About;
